const STATE_PREFIX = 'tth-retail-collection-v1:';
const STATE_AGE = 6 * 60 * 60 * 1000;
const MAX_SAVED_PAGES = 100;
const positive = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;

function cleanURL(value, base) {
  try {
    const url = new URL(value, base);
    if (url.origin !== new URL(base).origin || url.username || url.password || /[\\\r\n]/.test(String(value))) return null;
    url.hash = '';
    url.searchParams.delete('section_id'); url.searchParams.delete('sections');
    // Native facets can submit blank price bounds; Shopify omits those no-op
    // filters from pagination URLs. All nonempty filters remain significant.
    url.search = new URLSearchParams([...url.searchParams].filter(([key, value]) => !(key.startsWith('filter.') && value === ''))).toString();
    return url;
  } catch { return null; }
}

export function browseKey(value, base = value) {
  const url = cleanURL(value, base);
  if (!url) return '';
  url.searchParams.sort();
  return `${url.origin}${url.pathname}${url.search}`;
}

function resultKey(value, base, pageParam) {
  const url = cleanURL(value, base);
  if (!url) return '';
  url.searchParams.delete(pageParam);
  // Shopify's pagination links omit preview controls; these never change results.
  url.searchParams.delete('pb'); url.searchParams.delete('preview_theme_id');
  url.searchParams.sort();
  return `${url.origin}${url.pathname}${url.search}`;
}

export function nextPageURL(value, startURL, pageParam, expectedPage) {
  const url = cleanURL(value, startURL);
  if (!url || !positive(expectedPage) || url.searchParams.getAll(pageParam).length !== 1 || positive(url.searchParams.get(pageParam)) !== expectedPage || resultKey(url.href, startURL, pageParam) !== resultKey(startURL, startURL, pageParam)) return '';
  const start = new URL(startURL);
  for (const key of ['pb', 'preview_theme_id']) if (start.searchParams.has(key)) url.searchParams.set(key, start.searchParams.get(key));
  return url.href;
}

export function savedBrowseState(raw, startURL, pageParam, initialPage, now = Date.now()) {
  try {
    if (typeof raw !== 'string' || raw.length > 64000) return null;
    const state = JSON.parse(raw);
    if (state.key !== browseKey(startURL) || !Number.isFinite(state.savedAt) || state.savedAt > now || now - state.savedAt > STATE_AGE || !Number.isFinite(state.scrollY) || state.scrollY < 0 || state.scrollY > 10000000 || !Array.isArray(state.pages) || state.pages.length > MAX_SAVED_PAGES) return null;
    const pages = state.pages.map((value, index) => nextPageURL(value, startURL, pageParam, initialPage + index + 1));
    if (pages.some(value => !value)) return null;
    return { pages, scrollY: state.scrollY };
  } catch { return null; }
}

export class CollectionPager {
  constructor(root, environment = {}) {
    this.root = root;
    this.win = environment.window || window;
    this.fetch = environment.fetch || ((...args) => fetch(...args));
    this.parse = environment.parse || (html => new DOMParser().parseFromString(html, 'text/html'));
    this.now = environment.now || (() => Date.now());
    this.frame = environment.frame || (callback => this.win.requestAnimationFrame(callback));
    this.startURL = this.win.location.href;
    this.key = browseKey(this.startURL);
    this.pageParam = root.dataset.pageParam || 'page';
    this.page = positive(root.dataset.page);
    this.initialPage = this.page;
    this.grid = root.querySelector('#product-grid');
    this.link = root.querySelector('[data-collection-load-more]');
    this.status = root.querySelector('[data-collection-status]');
    this.pages = [];
    this.revision = 0;
    this.connected = true;
    this.autoLoads = 0;
    this.loadLabel = this.link?.textContent || "Load more products";
  }

  attach({ restore = false } = {}) {
    if (!this.page || !this.grid || !this.status || !this.key || !this.root.dataset.sectionId) return;
    this.events = new AbortController();
    const signal = this.events.signal;
    this.root.classList.add('tth-retail-collection--enhanced');
    this.root.addEventListener('click', event => {
      const link = event.target.closest('[data-collection-load-more]');
      if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!nextPageURL(link.href, this.startURL, this.pageParam, this.page + 1)) return;
      event.preventDefault();
      if (!this.restoring) { this.autoLoads = 0; void this.loadNext(); }
    }, { signal });
    const save = () => { if (!this.restoring) this.save(); };
    this.win.addEventListener('pagehide', save, { signal });
    this.win.addEventListener('scroll', () => {
      if (this.restoring || this.saveTimer) return;
      this.saveTimer = this.win.setTimeout(() => { this.saveTimer = null; save(); }, 250);
    }, { passive: true, signal });
    if (restore) void this.restore().finally(() => this.watchNext());
    else this.watchNext();
  }

  save() {
    if (!this.connected || this.restoring || browseKey(this.win.location.href) !== this.key) return;
    try {
      this.win.sessionStorage.setItem(STATE_PREFIX + this.key, JSON.stringify({ key: this.key, savedAt: this.now(), pages: this.pages.slice(0, MAX_SAVED_PAGES), scrollY: Math.max(0, this.win.scrollY || 0) }));
    } catch { /* Browsing still works if storage is unavailable. */ }
  }

  disconnect() {
    this.save();
    this.connected = false;
    this.revision++;
    this.request?.abort();
    this.preloaded?.request.abort();
    this.preloadObserver?.disconnect(); this.autoObserver?.disconnect();
    this.win.clearTimeout(this.saveTimer);
    this.events?.abort();
  }

  message(key, count) {
    const value = this.status?.dataset[key] || '';
    if (this.status) this.status.textContent = value.replace('__COUNT__', String(count ?? ''));
  }

  busy(value) {
    this.loading = value;
    this.grid?.setAttribute('aria-busy', String(value));
    this.link?.setAttribute('aria-disabled', String(value));
    if (this.link) this.link.textContent = value ? (this.status?.dataset.loading || 'Loading…') : this.loadLabel;
  }

  watchNext() {
    if (!this.connected || this.restoring || !this.link || this.link.hidden || typeof this.win.IntersectionObserver !== 'function') return;
    if (!this.preloadObserver) {
      const mobile = (this.win.innerWidth || 1280) < 750;
      this.preloadObserver = new this.win.IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) this.prefetchNext(); }, {rootMargin: mobile ? '700px' : '1000px'});
      this.autoObserver = new this.win.IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting) || this.autoLoads >= 2 || this.loading || this.restoring || this.root.classList.contains('tth-retail-collection--error') || this.win.document?.visibilityState === 'hidden' || this.win.document?.activeElement === this.link) return;
        this.autoLoads++; void this.loadNext({automatic:true});
      }, {rootMargin: mobile ? '180px' : '300px'});
    }
    this.preloadObserver.unobserve(this.link); this.preloadObserver.observe(this.link);
    this.autoObserver.unobserve(this.link); this.autoObserver.observe(this.link);
  }

  async fetchHTML(pageURL, controller) {
    const url = new URL(pageURL); url.searchParams.set('section_id', this.root.dataset.sectionId);
    const response = await this.fetch(url.href, {signal:controller.signal,credentials:'same-origin',headers:{Accept:'text/html'}});
    if (!response.ok || response.url && !nextPageURL(response.url,this.startURL,this.pageParam,positive(new URL(pageURL).searchParams.get(this.pageParam)))) throw new Error('Invalid collection response');
    const html = await response.text();
    if (typeof html !== 'string' || html.length > 4000000) throw new Error('Invalid collection response');
    return html;
  }

  prefetchNext() {
    if (!this.connected || this.restoring || this.loading || this.link?.hidden || this.win.document?.visibilityState === 'hidden' || this.win.navigator?.connection?.saveData || this.root.classList.contains('tth-retail-collection--error') || browseKey(this.win.location.href) !== this.key) return;
    const url = this.link && nextPageURL(this.link.href,this.startURL,this.pageParam,this.page+1);
    if (!url || this.preloaded?.url === url) return;
    this.preloaded?.request.abort();
    const request=new AbortController(); const timeout=this.win.setTimeout(()=>request.abort(),15000);
    this.preloaded={url,request,promise:this.fetchHTML(url,request).catch(()=>null).finally(()=>this.win.clearTimeout(timeout))};
  }

  async loadNext({ restoring = false, expectedURL = '', automatic = false } = {}) {
    if (!this.connected || this.loading || !this.link || this.link.hidden || this.root.closest('[aria-busy="true"]')) return false;
    const pageURL = nextPageURL(this.link.href, this.startURL, this.pageParam, this.page + 1);
    if (!pageURL || expectedURL && browseKey(pageURL) !== browseKey(expectedURL)) return false;
    if (!restoring) this.restoreCancelled = false;
    const revision = ++this.revision;
    const cached = this.preloaded?.url === pageURL ? this.preloaded : null;
    const controller = this.request = cached?.request || new AbortController();
    if (cached) this.preloaded = null;
    const timeout = this.win.setTimeout(() => controller.abort(), 15000);
    const requestedPage = this.page + 1;
    this.busy(true); this.message('loading');
    this.root.classList.remove('tth-retail-collection--error');
    try {
      const html = cached ? await cached.promise : await this.fetchHTML(pageURL, controller);
      if (typeof html !== 'string') throw new Error('Prefetch unavailable');
      const doc = this.parse(html);
      const host = doc.querySelector('tth-retail-collection');
      const grid = host?.querySelector('#product-grid');
      if (!host || host.dataset.sectionId !== this.root.dataset.sectionId || positive(host.dataset.page) !== requestedPage || host.dataset.pageParam !== this.pageParam || !grid || grid.tagName !== 'UL') throw new Error('Incomplete collection results');
      const rows = [...grid.children];
      if (!rows.length || rows.some(row => !/^[1-9]\d{0,19}$/.test(row.dataset.collectionProductId || ''))) throw new Error('Invalid collection products');
      const next = host.querySelector('[data-collection-load-more]');
      const nextURL = next ? nextPageURL(next.href, this.startURL, this.pageParam, requestedPage + 1) : '';
      if (next && !nextURL) throw new Error('Invalid continuation');
      if (!this.connected || !this.root.isConnected || revision !== this.revision || controller.signal.aborted || browseKey(this.win.location.href) !== this.key) return false;
      const seen = new Set([...this.grid.children].map(row => row.dataset.collectionProductId));
      const added = rows.filter(row => { const id = row.dataset.collectionProductId; if (seen.has(id)) return false; seen.add(id); return true; });
      for (const row of added) {
        row.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.removeAttribute('fetchpriority'); });
        row.querySelectorAll('[data-tth-badge-shell]').forEach(badge => { badge.style.visibility = 'visible'; badge.style.opacity = '1'; });
      }
      this.grid.append(...added);
      const navigation = this.root.querySelector('[data-collection-pagination]');
      const sourceNavigation = host.querySelector('[data-collection-pagination]');
      if (navigation && sourceNavigation) navigation.replaceChildren(...sourceNavigation.childNodes);
      this.page = requestedPage; this.pages.push(pageURL);
      if (nextURL) this.link.href = nextURL;
      else this.link.hidden = true;
      this.message(nextURL ? 'loaded' : 'allLoaded', added.length);
      if (!restoring) {
        if (!automatic) added[0]?.querySelector('a[href], button')?.focus({ preventScroll: true });
        this.save();
      }
      return true;
    } catch {
      if (this.connected && revision === this.revision && !this.restoreCancelled) {
        this.root.classList.add('tth-retail-collection--error');
        this.message('error');
      }
      return false;
    } finally {
      this.win.clearTimeout(timeout);
      if (revision === this.revision) { this.busy(false); this.watchNext(); }
    }
  }

  async restore() {
    let raw;
    try { raw = this.win.sessionStorage.getItem(STATE_PREFIX + this.key); } catch { return; }
    const saved = savedBrowseState(raw, this.startURL, this.pageParam, this.initialPage, this.now());
    if (!saved) return;
    this.restoring = true; this.restoreCancelled = false;
    const interruptions = new AbortController();
    const cancel = () => { this.restoreCancelled = true; this.request?.abort(); };
    ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => this.win.addEventListener(type, cancel, { once: true, passive: true, signal: interruptions.signal }));
    try {
      for (const url of saved.pages) {
        if (this.restoreCancelled || !await this.loadNext({ restoring: true, expectedURL: url })) return;
      }
      if (!this.connected || this.restoreCancelled) return;
      await new Promise(resolve => this.frame(() => this.frame(resolve)));
      if (this.connected && !this.restoreCancelled) {
        this.win.scrollTo({ top: saved.scrollY, behavior: 'instant' });
        this.message('restored');
      }
    } finally {
      interruptions.abort(); this.restoring = false;
      this.autoLoads = Math.min(2, this.pages.length);
      if (this.connected) this.save();
    }
  }
}

if (typeof window !== 'undefined' && window.customElements) {
  const restoreIntents = new Set();
  const type = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;
  if (type === 'reload' || type === 'back_forward') restoreIntents.add(browseKey(window.location.href));
  window.addEventListener('popstate', () => restoreIntents.add(browseKey(window.location.href)));
  class TthRetailCollection extends HTMLElement {
    connectedCallback() {
      // Facets replace the grid before updating history. Wait for that synchronous
      // replacement to finish so a new instance is keyed to the new filter query.
      queueMicrotask(() => {
        if (!this.isConnected || this.pager) return;
        const key = browseKey(window.location.href);
        this.pager = new CollectionPager(this);
        this.pager.attach({ restore: restoreIntents.delete(key) });
      });
    }
    disconnectedCallback() { this.pager?.disconnect(); this.pager = null; }
  }
  if (!window.customElements.get('tth-retail-collection')) window.customElements.define('tth-retail-collection', TthRetailCollection);
}

